/**
 * Pending Universe Request banner — Reporter / Core variant.
 *
 * Surfaces when the user lands on Reporter via a link with
 * ?universe_request_id=X (an internal-fulfillment invite sent by Core when a
 * recurring schedule fires against an internal department). It tells the user
 * which request is pending and which sections it covers, and lets the
 * recipient report the universe as already submitted (operator order
 * 2026-10-18): that pauses the reminders and asks compliance to confirm.
 *
 * The recipient's door (/api/universe-ack) answers first, because a department
 * user holds no Universe Requests module; compliance's route is the fallback.
 */

import React, { useEffect, useState } from 'react';
import UniverseRequestAcknowledge from './UniverseRequestAcknowledge.jsx';

const API = process.env.REACT_APP_API_URL || '';

function readQueryParam(name) {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

export default function PendingUniverseRequestBanner({ consumerAppFilter = 'reporter' }) {
  const [request, setRequest] = useState(null);
  const [mode, setMode] = useState('recipient');
  const [hidden, setHidden] = useState(false);
  const id = readQueryParam('universe_request_id');
  const wantsAck = readQueryParam('ack') === '1';

  useEffect(() => {
    if (!id) return;
    const read = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => (r.ok ? r.json() : null)).catch(() => null);
    // The door says which mode answered: a named recipient pauses the
    // reminders; a compliance user marks the universes received. The
    // compliance fallback route carries no mode, so it is compliance.
    read(`/api/universe-ack/request/${encodeURIComponent(id)}`)
      .then(d => (d?.request ? { ...d, mode: d.mode || 'recipient' } : read(`/api/universe-requests/${encodeURIComponent(id)}`).then(c => (c?.request ? { ...c, mode: 'compliance' } : null))))
      .then(d => { setRequest(d?.request || null); setMode(d?.mode || 'recipient'); })
      .catch(() => setRequest(null));
  }, [id]);

  if (!id || hidden || !request) return null;
  if (!['sent', 'in_progress'].includes(request.status)) return null;

  const items = (request.items || []).filter(i => i.consumer_app === consumerAppFilter);
  if (items.length === 0) return null;
  const owed = items.filter(i => !['received', 'accepted'].includes(i.status));

  return (
    <div style={{
      background: 'linear-gradient(90deg, rgba(220,38,38,0.08), rgba(220,38,38,0.02))',
      borderLeft: '3px solid #b91c1c',
      padding: '12px 16px',
      margin: '12px 0',
      fontSize: 13,
      color: '#1e293b',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#b91c1c', marginBottom: 4 }}>
          Pending Universe Request{request.sequence_number ? ` · cycle ${request.sequence_number}` : ''}
        </div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{request.title}</div>
        <div style={{ fontSize: 12, color: '#475569' }}>
          {items.length} item{items.length === 1 ? '' : 's'} expected:&nbsp;
          {items.map(i => ['received', 'accepted'].includes(i.status) ? `${i.catalog_label} (received)` : i.catalog_label).join(' · ')}
          {request.reporting_period_start && request.reporting_period_end && (
            <> · period {request.reporting_period_start} → {request.reporting_period_end}</>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
          Upload your file{items.length > 1 ? 's' : ''} using the form below. Compliance will see your submission against this request.
        </div>
        {owed.length > 0
          ? <UniverseRequestAcknowledge request={request} autoOpen={wantsAck} compact mode={mode} consumerApp={consumerAppFilter} />
          : <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>Everything this request asks of Reporter is in. The request waits on its other items.</div>}
      </div>
      <button
        onClick={() => setHidden(true)}
        style={{
          background: 'transparent', border: 'none', color: '#64748b',
          fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1,
        }}
        title="Dismiss banner"
      >×</button>
    </div>
  );
}
